import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from '../../components/Footer';
import { ArrowLeft, MessageSquare, Mail, ChevronDown, CheckCircle, AlertCircle } from 'react-feather';
import { useNavigate } from 'react-router-dom';

export default function Support() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: "¿No encuentro mi código QR?",
      a: "Revisa tu bandeja de entrada (y spam) buscando 'AFTR'. También puedes verlo ingresando a tu cuenta en la sección 'Mis Entradas' dentro de la app."
    },
    {
      q: "¿Puedo transferir mi entrada a un amigo?",
      a: "Por seguridad, los QRs son personales. Sin embargo, si compraste una 'Mesa' o 'Box', tú gestionas el ingreso de tus invitados al llegar al local."
    },
    {
      q: "¿Cómo solicito un reembolso?",
      a: "AFTR es intermediario. Debes contactar directamente al organizador del evento (su info está en la descripción del evento). Nosotros solo procesamos reembolsos autorizados por ellos."
    },
    {
      q: "¿Qué hago si el evento se canceló?",
      a: "Te notificaremos por correo electrónico con los pasos a seguir. Generalmente, el reembolso es automático en estos casos, pero depende de los plazos del banco (7-15 días hábiles)."
    }
  ];

  const AccordionItem = ({ q, a }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className="border-b border-white/5 last:border-0">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="w-full py-6 flex justify-between items-center text-left hover:text-white/80 transition-colors"
        >
          <span className="text-lg font-medium text-white">{q}</span>
          <ChevronDown 
            className={`text-white/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <p className="pb-6 text-white/50 leading-relaxed font-light">
                {a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-white selection:text-black">
      
      {/* TEXTURA DE FONDO */}
      <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.04] mix-blend-overlay" 
           style={{ backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")' }}>
      </div>

      {/* GRADIENTE AMBIENTAL */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[1000px] h-[400px] bg-white/5 blur-[120px] rounded-full pointer-events-none" />

      {/* HEADER NAV */}
      <div className="fixed top-0 left-0 w-full z-40 px-6 py-6 flex justify-between items-center pointer-events-none">
        <button 
            onClick={() => navigate(-1)} 
            className="pointer-events-auto flex items-center gap-2 text-xs md:text-sm font-bold tracking-widest text-white/50 hover:text-white transition-colors group uppercase bg-black/20 backdrop-blur-md px-4 py-2 rounded-full border border-white/5"
        >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            Volver
        </button>
      </div>

      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">

          {/* TITULO */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs font-mono text-white/60 mb-6">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                SISTEMAS OPERATIVOS
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              Centro de Ayuda.
            </h1>
            <p className="text-lg text-white/50 max-w-xl mx-auto font-light">
              Respuestas rápidas para que no pierdas tiempo y sigas disfrutando de la noche.
            </p>
          </motion.div>

          {/* GRID DE CONTACTO */}
          <motion.div 
            className="grid md:grid-cols-2 gap-4 mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* WhatsApp - El canal más rápido */}
            <a href="https://wa.me/51941549341" target="_blank" rel="noreferrer" className="group bg-[#0A0A0A] border border-white/10 hover:border-white/30 p-8 rounded-2xl transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                    <MessageSquare size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Chat Soporte</h3>
                <p className="text-white/40 text-sm mb-4">La forma más rápida. Respondemos al instante.</p>
                <span className="text-sm font-bold border-b border-white/30 pb-0.5 group-hover:border-white transition-colors">Abrir WhatsApp &rarr;</span>
            </a>

            {/* Email - Para temas formales */}
            <a href="mailto:soporte@goclub.social" className="group bg-[#0A0A0A] border border-white/10 hover:border-white/30 p-8 rounded-2xl transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                    <Mail size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">Correo Electrónico</h3>
                <p className="text-white/40 text-sm mb-4">Para reembolsos o temas técnicos complejos.</p>
                <span className="text-sm font-bold border-b border-white/30 pb-0.5 group-hover:border-white transition-colors">Enviar Email &rarr;</span>
            </a>
          </motion.div>

          {/* STATUS BAR - Fake pero útil para confianza */}
          <div className="mb-20 p-4 rounded-xl bg-green-500/5 border border-green-500/20 flex items-center gap-4">
            <CheckCircle className="text-green-500 shrink-0" size={20} />
            <div className="text-sm">
                <span className="text-green-400 font-bold block mb-0.5">Todos los sistemas operativos</span>
                <span className="text-white/40">Pagos, Generación de QRs y Accesos funcionando correctamente.</span>
            </div>
          </div>

          {/* FAQ ACCORDION */}
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">Preguntas Frecuentes</h2>
            <div className="space-y-2">
                {faqs.map((faq, index) => (
                    <AccordionItem key={index} q={faq.q} a={faq.a} />
                ))}
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}